/**
 * T-55: questions the software factory's AI builder asks on a task mirrored from jflo. The factory
 * posts a comment holding one fenced block, the same one its coding agent writes (software-factory's
 * shared/factory-questions.ts, kept in step with it):
 *
 * ```factory-questions
 * { "ask": "sam@example.com", "questions": [{ "id": "scope", "header": "Scope", "question": "…",
 *   "options": [{ "label": "Just the API", "description": "…" }], "multiSelect": false }] }
 * ```
 *
 * The task shows it as a question card (FactoryQuestionsCard) and, when `ask` names someone on the
 * team, waits on them. Their answers go back as one comment in the factory's own Answers format, so
 * the factory reads them the way it reads answers given on its own board.
 *
 * T-99 (the factory's SF-T-308): the one decisions block. `phase` says when it's for: `grill` (the
 * default: questions before building, as above) or `wrap` (the end of a turn: what to do next, read as a
 * round of suggestions by findFactorySuggestions, with options). Items (`questions`, or `items`) can carry
 * `detail`, the `recommended` option, and for wrap items `change`, `without` and `recommend`. A
 * `factory-decisions` block reads the same. A grill answer can be Skip (the agent's recommendation) or
 * Backlog (the agent makes a task of it for later).
 */

import { TASK_CHAT_MAX } from './types/tasks';
import { indentFollowingLines, readFollowingLines } from './list-item-lines';

export const FACTORY_QUESTIONS_LANG = 'factory-questions';
/** T-99: another name for the same block, read as factory-questions. */
export const FACTORY_DECISIONS_LANG = 'factory-decisions';

/** T-99: before building (questions) or at the end of a turn (what to do next). */
export type DecisionPhase = 'grill' | 'wrap';

/** Most questions in one round, options per question, and characters per piece of text. */
export const FACTORY_QUESTIONS_MAX = 8;
export const FACTORY_OPTIONS_MAX = 8;
/** Characters per piece of text: a question, an option's description, or one answer written on the card. */
export const TEXT_MAX = 1000;
/** Characters in a wrap item's change or without, and in a round's progress line. */
const NOTE_MAX = 500;
const DETAIL_MAX = 2000;
/** Most items in a wrap round, as in a factory-suggestions block. */
const WRAP_ITEMS_MAX = 10;

export interface FactoryQuestionOption {
  label: string;
  description?: string;
}

export interface FactoryQuestion {
  id: string;
  /** A short label, e.g. "Scope". */
  header?: string;
  question: string;
  /** T-99: more on the question: why it matters, what's at stake. */
  detail?: string;
  /** None: answered in words. */
  options: FactoryQuestionOption[];
  multiSelect: boolean;
  /** T-99: the label of the option the agent recommends (what Skip means in grill). */
  recommended?: string;
  /** T-99, wrap items: what building it would change, and what stays without it. */
  change?: string;
  without?: string;
  /** T-99, wrap items: the action the agent would pick, as written (yes, branch, backlog, skip). */
  recommend?: string;
}

export interface FactoryQuestions {
  /** Who should answer: an email or a name, as the agent wrote it. Null: the person who asked the AI builder. */
  ask: string | null;
  /** T-99: grill (questions before building; also when it's left out) unless the block says wrap. */
  phase?: DecisionPhase;
  /** T-99: how close the grilling is: what's still open (a grill round's line on the card). */
  progress?: string;
  questions: FactoryQuestion[];
}

/** T-99: an option's label without the "(Recommended)" an agent may write in it. */
export const plainOptionLabel = (label: string) => label.replace(/\s*\(recommended\)\s*$/i, '').trim();

const text = (v: unknown, max = TEXT_MAX): string => (typeof v === 'string' ? v.trim().slice(0, max) : '');

/** The block's JSON, checked and trimmed; null when it isn't usable. */
export function parseFactoryQuestionsJson(raw: string): FactoryQuestions | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  // A bare list of questions is accepted too.
  const obj = (Array.isArray(data) ? { questions: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object') return null;
  // T-99: `items` (or `decisions`) is the same list.
  // An empty one doesn't hide the next (`questions: []` beside `items`).
  const lists = [obj.questions, obj.items, obj.decisions].filter(Array.isArray) as unknown[][];
  const list = lists.find((l) => l.length) ?? lists[0];
  if (!list) return null;
  const phase: DecisionPhase =
    typeof obj.phase === 'string' ? (obj.phase.trim().toLowerCase() === 'wrap' ? 'wrap' : 'grill') : looksLikeWrap(list) ? 'wrap' : 'grill';
  const questions: FactoryQuestion[] = [];
  const ids = new Set<string>();
  for (const [i, item] of list.slice(0, phase === 'wrap' ? WRAP_ITEMS_MAX : FACTORY_QUESTIONS_MAX).entries()) {
    const q = (typeof item === 'string' ? { question: item } : (item ?? {})) as Record<string, unknown>;
    // T-99: a wrap item names itself with a title, as a suggestion does.
    const question = text(q.question) || text(q.title);
    if (!question) continue;
    const options = parseQuestionOptions(q.options);
    const header = text(q.header, 60);
    const detail = text(q.detail ?? q.description, DETAIL_MAX);
    const change = text(q.change, NOTE_MAX);
    const without = text(q.without, NOTE_MAX);
    const recommend = text(q.recommend, 40);
    const recommended = recommendedOption(q.recommended, options);
    // Ids key the answers on the card: each one its own (jflo, T-55).
    let id = text(q.id, 60) || `q${i + 1}`;
    if (ids.has(id)) id = `${id}-${i + 1}`;
    ids.add(id);
    questions.push({
      id,
      ...(header ? { header } : {}),
      question,
      ...(detail ? { detail } : {}),
      options,
      multiSelect: q.multiSelect === true,
      ...(recommended ? { recommended } : {}),
      ...(change ? { change } : {}),
      ...(without ? { without } : {}),
      ...(recommend ? { recommend } : {}),
    });
  }
  if (!questions.length) return null;
  const progress = text(obj.progress, NOTE_MAX);
  return { ask: text(obj.ask, 200) || null, ...(phase === 'wrap' ? { phase } : {}), ...(progress ? { progress } : {}), questions };
}

/** An item's options, checked and trimmed (a wrap item's too). */
export function parseQuestionOptions(raw: unknown): FactoryQuestionOption[] {
  return (Array.isArray(raw) ? raw : [])
    .slice(0, FACTORY_OPTIONS_MAX)
    .map((o): FactoryQuestionOption | null => {
      if (typeof o === 'string') return text(o, 200) ? { label: text(o, 200) } : null;
      const opt = (o ?? {}) as Record<string, unknown>;
      const label = text(opt.label, 200);
      if (!label) return null;
      const description = text(opt.description);
      return description ? { label, description } : { label };
    })
    .filter((o): o is FactoryQuestionOption => o !== null);
}

/**
 * T-99: a block that leaves its phase out reads as wrap only when every item is plainly a suggestion: a
 * title, no question. Anything with a question stays grill, so a run that asks is never taken for one that's done.
 */
function looksLikeWrap(list: unknown[]): boolean {
  const items = list.filter((x): x is Record<string, unknown> => Boolean(x) && typeof x === 'object');
  if (!items.length || items.length !== list.length) return false;
  const has = (q: Record<string, unknown>, k: string) => typeof q[k] === 'string' && (q[k] as string).trim() !== '';
  return items.every((q) => has(q, 'title') && !has(q, 'question'));
}

/**
 * T-99: the recommended option's label: `recommended` as a label or an option's place (from 0), else the
 * first option whose label says "(Recommended)", as agents have written it. Undefined when there's none.
 */
export function recommendedOption(value: unknown, options: FactoryQuestionOption[]): string | undefined {
  // An index past the options falls through to a label marked "(Recommended)".
  if (typeof value === 'number' && Number.isInteger(value) && options[value]) return options[value].label;
  if (typeof value === 'string' && value.trim()) {
    const want = plainOptionLabel(value).toLowerCase();
    const found = options.find((o) => plainOptionLabel(o.label).toLowerCase() === want);
    if (found) return found.label;
  }
  return options.find((o) => /\(recommended\)\s*$/i.test(o.label))?.label;
}

/**
 * The first fenced block in a message whose language is `lang` (```lang or ~~~lang), with where it
 * sits: its body, and the offsets of the whole block. Null when there's none. Also used for
 * factory-suggestions.
 */
export function findFencedBlock(message: string, lang: string): { body: string; start: number; end: number } | null {
  const body = message.replace(/\r\n?/g, '\n');
  const open = new RegExp('^ {0,3}(`{3,}|~{3,})[ \\t]*' + lang + '[ \\t]*$', 'm').exec(body);
  if (!open) return null;
  const fence = open[1];
  const bodyStart = open.index + open[0].length + 1;
  const close = new RegExp(`^ {0,3}${fence[0] === '`' ? '`' : '~'}{${fence.length},}[ \\t]*$`, 'm').exec(body.slice(bodyStart));
  const bodyEnd = close ? bodyStart + close.index : body.length;
  return { body: body.slice(bodyStart, bodyEnd), start: open.index, end: close ? bodyStart + close.index + close[0].length : body.length };
}

/**
 * T-99: the first decisions block in a message (factory-questions, or factory-decisions), either phase,
 * with where it sits; null when there's none (or it's broken).
 */
export function findDecisionsBlock(message: string): { questions: FactoryQuestions; start: number; end: number } | null {
  for (const lang of [FACTORY_QUESTIONS_LANG, FACTORY_DECISIONS_LANG]) {
    const block = findFencedBlock(message, lang);
    const questions = block ? parseFactoryQuestionsJson(block.body) : null;
    if (block && questions) return { questions, start: block.start, end: block.end };
  }
  return null;
}

/**
 * The message's questions: its decisions block in the grill phase, with where it sits; null when there's
 * none (or it's broken). T-99: a block in the wrap phase is a round of suggestions (findFactorySuggestions).
 */
export function findFactoryQuestions(message: string): { questions: FactoryQuestions; start: number; end: number } | null {
  const found = findDecisionsBlock(message);
  return found && found.questions.phase !== 'wrap' ? found : null;
}

/** One question's answer: the options picked and anything written. */
export interface FactoryAnswer {
  picked: string[];
  other: string;
  /** T-99: Skip was pressed: the agent goes with its recommendation. */
  skipped?: boolean;
  /** T-99: Backlog was pressed: not for this build; the agent makes a task of it (`other` is a note for it). */
  backlog?: boolean;
}

/**
 * A question's answer, read as the map's own entry only: an id like `constructor` or `__proto__`
 * mustn't find what every object inherits (jflo, T-55).
 */
export function answerFor(answers: Record<string, FactoryAnswer | undefined>, id: string): FactoryAnswer | undefined {
  return Object.prototype.hasOwnProperty.call(answers, id) ? answers[id] : undefined;
}

/** True when every question has an answer, or was skipped or sent to the Backlog (T-99). */
export function answersComplete(questions: FactoryQuestion[], answers: Record<string, FactoryAnswer | undefined>): boolean {
  return questions.every((q) => {
    const a = answerFor(answers, q.id);
    return Boolean(a && (a.skipped || a.backlog || a.picked.length || a.other.trim()));
  });
}

/** T-99: a skipped question's answer, as the answers message writes it (the factory's SKIPPED_ANSWER). */
export const SKIPPED_ANSWER = '_(skipped)_';
/** T-99: a question sent to the Backlog, with any note after it (`_(backlog)_: …`), as the factory writes it. */
export const BACKLOG_ANSWER = '_(backlog)_';

/** T-99: for an answer from parseFactoryAnswers sent to the Backlog, its note ('' for none); null for any other answer. */
export function backlogAnswerNote(answer: string | null | undefined): string | null {
  if (!answer?.startsWith(BACKLOG_ANSWER)) return null;
  return answer.slice(BACKLOG_ANSWER.length).replace(/^:\s*/, '').trim();
}

/** How far an answer's further lines are indented: under the text after `   → ` (as the factory's). */
const ANSWER_INDENT = 5;

/**
 * The answers as the chat message the person posts (and the agent reads). T-99: an answer of several lines
 * keeps them, indented under the arrow's text, as the factory writes them.
 */
export function formatFactoryAnswers(questions: FactoryQuestion[], answers: Record<string, FactoryAnswer | undefined>): string {
  const lines = ['**Answers**', ''];
  questions.forEach((q, i) => {
    const a = answerFor(answers, q.id);
    const other = a?.other.trim() ?? '';
    const parts = [...(a?.picked ?? []), ...(other ? [other] : [])];
    let answer: string;
    if (a?.backlog) answer = other ? `${BACKLOG_ANSWER}: ${other}` : BACKLOG_ANSWER;
    else if (a?.skipped) answer = SKIPPED_ANSWER;
    else answer = parts.length ? parts.join('; ') : '_(no answer)_';
    lines.push(`${i + 1}. ${q.header ? `**${q.header}:** ` : ''}${q.question.replace(/\s*\n+\s*/g, ' ')}`);
    lines.push(`   → ${indentFollowingLines(answer, ANSWER_INDENT)}`);
  });
  return lines.join('\n');
}

/**
 * Reads back a message formatFactoryAnswers wrote, one answer per question in order, so the
 * task's Decisions can show each question with its answer. Null when the message isn't one.
 * An unanswered question reads as null.
 */
export function parseFactoryAnswers(message: string): (string | null)[] | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  if (lines[0]?.trim() !== '**Answers**') return null;
  const answers: (string | null)[] = [];
  for (let i = 1; i < lines.length; ) {
    const line = lines[i++];
    if (/^\d+\.\s/.test(line)) answers.push(null);
    else {
      const answer = /^\s+→\s?(.*)$/.exec(line);
      if (answer && answers.length && answers[answers.length - 1] === null) {
        // T-99: the answer's further lines, indented under it.
        const more = readFollowingLines(lines, i, ANSWER_INDENT);
        i = more.next;
        const text = [answer[1].trim(), more.text].filter(Boolean).join('\n');
        answers[answers.length - 1] = text && text !== '_(no answer)_' ? text : null;
      }
    }
  }
  return answers.length ? answers : null;
}

/**
 * The first line of a reply to a round written in words (the card's "Reply in words"
 * button), in place of picking an answer per question. Like an Answers message, it closes the round
 * it replies to; a plain comment never does.
 */
const QUESTIONS_REPLY_HEAD = '**Reply to the questions**';

/**
 * The longest reply in words: what fits in a task comment (TASK_CHAT_MAX) with the heading, so the server never cuts it short.
 */
export const QUESTIONS_REPLY_MAX = TASK_CHAT_MAX - QUESTIONS_REPLY_HEAD.length - 2;

/** A reply in words to a round of questions, as the chat message the person posts (and the agent reads). */
export function formatQuestionsReply(text: string): string {
  return `${QUESTIONS_REPLY_HEAD}\n\n${text.trim()}`;
}

/** The text of a reply formatQuestionsReply wrote; null when the message isn't one. */
export function parseQuestionsReply(message: string): string | null {
  const body = message.replace(/\r\n?/g, '\n').trim();
  if (!body.startsWith(QUESTIONS_REPLY_HEAD)) return null;
  const rest = body.slice(QUESTIONS_REPLY_HEAD.length);
  if (rest && !rest.startsWith('\n')) return null;
  return rest.trim();
}
